import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline'
import { useState } from 'react'

import { CarrierConnectionExpanded } from '@engine/models'

import { KeyValueDisplay, KeyValueItem } from '#components/KeyValueDisplay'

interface CarrierCredentialDetailsProps {
  credential: CarrierConnectionExpanded
}

const obfuscateValue = (value: string): string => '•'.repeat(value.length)

const CarrierCredentialDetails = ({
  credential,
}: CarrierCredentialDetailsProps) => {
  const [revealedFields, setRevealedFields] = useState<Set<string>>(new Set())

  if (!credential) {
    return <div>No credential selected.</div>
  }

  const toggleReveal = (key: string) => {
    const newRevealed = new Set(revealedFields)
    if (newRevealed.has(key)) {
      newRevealed.delete(key)
    } else {
      newRevealed.add(key)
    }
    setRevealedFields(newRevealed)
  }

  const baseItems: KeyValueItem[] = [
    {
      label: 'Credential ID',
      value: credential._id,
    },
    {
      label: 'Account',
      value: (
        <>
          <span>{credential.accountName}</span>{' '}
          <span className="text-xs opacity-60">{credential.accountId}</span>
        </>
      ),
    },
  ]

  const detailItems: KeyValueItem[] =
    credential.details && Object.keys(credential.details).length > 0
      ? Object.entries(credential.details).map(([key, value]) => {
          const isRevealed = revealedFields.has(key)
          const isString = typeof value === 'string'
          const displayValue =
            isString && !isRevealed ? obfuscateValue(value) : String(value)

          return {
            label: key.replace(/([A-Z])/g, ' $1').trim(),
            value: (
              <div className="flex items-center justify-between gap-2">
                <div className="flex-1">
                  {typeof value === 'boolean' ? (
                    <span
                      className={`badge ${value ? 'badge-success' : 'badge-ghost'}`}
                    >
                      {value ? 'Yes' : 'No'}
                    </span>
                  ) : typeof value === 'object' ? (
                    <pre className="mt-1 overflow-auto rounded border border-base-300 bg-base-200 p-2 text-xs">
                      {JSON.stringify(value, null, 2)}
                    </pre>
                  ) : (
                    <span className="`text-sm">{displayValue}</span>
                  )}
                </div>
                {isString && (
                  <button
                    className="btn btn-ghost btn-xs flex items-center"
                    onClick={() => toggleReveal(key)}
                    aria-label={isRevealed ? 'Hide value' : 'Show value'}
                  >
                    {isRevealed ? (
                      <EyeSlashIcon className="w-4 h-4" />
                    ) : (
                      <EyeIcon className="w-4 h-4" />
                    )}
                  </button>
                )}
              </div>
            ),
            span: 2,
          }
        })
      : []

  return (
    <div>
      <KeyValueDisplay items={baseItems} className="mb-4" />

      {detailItems.length > 0 && (
        <div className="border-t border-base-200 pt-4">
          <KeyValueDisplay items={detailItems} />
        </div>
      )}
    </div>
  )
}

export { CarrierCredentialDetails }
